/**
 * Inactivity Warning Modal
 *
 * Displays a warning when the user has been inactive for too long.
 * Provides option to continue session or logout immediately.
 */

import { Clock, LogOut } from 'lucide-react';

interface InactivityWarningModalProps {
  isOpen: boolean;
  secondsRemaining: number;
  onContinue: () => void;
  onLogout: () => void;
}

export function InactivityWarningModal({
  isOpen,
  secondsRemaining,
  onContinue,
  onLogout,
}: InactivityWarningModalProps) {
  if (!isOpen) return null;

  const minutes = Math.floor(secondsRemaining / 60);
  const seconds = secondsRemaining % 60;
  const timeDisplay = minutes > 0
    ? `${minutes}:${seconds.toString().padStart(2, '0')}`
    : `${seconds}s`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black bg-opacity-50" />

      {/* Modal */}
      <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
        <div className="flex items-center justify-center mb-4">
          <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center">
            <Clock size={32} className="text-yellow-600" />
          </div>
        </div>

        <h2 className="text-xl font-bold text-gray-900 text-center mb-2">
          Session Timeout Warning
        </h2>

        <p className="text-gray-600 text-center mb-4">
          You have been inactive for a while. For security purposes, you will be
          automatically logged out in:
        </p>

        <div className="text-4xl font-bold text-red-600 text-center mb-6">
          {timeDisplay}
        </div>

        <div className="flex gap-3">
          <button
            onClick={onLogout}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
          >
            <LogOut size={18} />
            Logout Now
          </button>
          <button
            onClick={onContinue}
            className="flex-1 px-4 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium"
          >
            Continue Session
          </button>
        </div>

        <p className="text-xs text-gray-500 text-center mt-4">
          Click anywhere or press any key to dismiss this warning and continue your session.
        </p>
      </div>
    </div>
  );
}
